import React from 'react';
import { useLocation } from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import {
	CancellationData,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import { useRouteMatch } from '../../../util/useRouteMatch';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import TreeNode, {
	TreeData,
	TreeItemData,
	TreeItemInterface,
} from '../../layouts/SidePanel/TreeNode';

interface Props {
	authData: AuthData;
	cancellationData: CancellationData;
}

export interface AcceptedReinstatementRoutes {
	INVOICES: string;
	INVOICE: string;
}

export const ACCEPTED_REINSTATEMENT_ROUTES: AcceptedReinstatementRoutes = {
	INVOICES:
		'/policy/:policyId/cancellations/:cancellationId/reinstatements/:reinstatementId/invoices',
	INVOICE:
		'/policy/:policyId/cancellations/:cancellationId/reinstatements/:reinstatementId/invoices/:invoiceId',
};

const ReinstatementSidebar = (props: Props) => {
	const { authData, cancellationData } = props;
	const { hasPermission } = authData;
	const { policy, selectedCancellation } = cancellationData;

	const { reinstatement } = selectedCancellation;
	const { pathname } = useLocation();
	const routeMatch = useRouteMatch(
		Object.keys(ACCEPTED_REINSTATEMENT_ROUTES).map(
			(key) =>
				ACCEPTED_REINSTATEMENT_ROUTES[key as keyof AcceptedReinstatementRoutes],
		),
	);

	if (!policy || !selectedCancellation || !reinstatement) {
		return <LargeCenterSpinner />;
	}

	const routeName = pathname.split('/').pop();
	const cancellationLink = `/policy/${policy.locator}/cancellations/${selectedCancellation.locator}`;
	const reinstatementLink = `${cancellationLink}/reinstatements/${reinstatement.locator}`;
	const overviewLink = `${reinstatementLink}/details`;
	const priceBreakdownLink = `${reinstatementLink}/pricing`;
	const documentsLink = `${reinstatementLink}/documents`;
	const invoicesLink = `${reinstatementLink}/invoices`;

	const getRootItemAccepted = () => {
		const children = ['Details', 'Price Breakdown', 'Documents'];

		if (hasPermission('invoice.fetch')) {
			children.push('Invoices');
		}

		return {
			id: 'root',
			children,
			hasChildren: true,
			isExpanded: true,
			isChildrenLoading: false,
			data: {
				title: 'root',
			},
		} as TreeItemInterface;
	};

	const getItemsAccepted = () => {
		const isDetailsSelected =
			routeName === 'details' || routeName === 'auxData';

		const items: TreeItemData = {
			root: getRootItemAccepted(),
			Details: {
				id: 'Details',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Details',
					link: overviewLink,
					selected: isDetailsSelected,
				},
			},
			'Price Breakdown': {
				id: 'Price Breakdown',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Price Breakdown',
					link: priceBreakdownLink,
					selected: routeName === 'pricing',
				},
			},
			Documents: {
				id: 'Documents',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Documents',
					link: documentsLink,
					selected: routeName === 'documents',
				},
			},
			Invoices: {
				id: 'Invoices',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Invoices',
					link: invoicesLink,
					selected:
						routeMatch?.pattern?.path ===
							ACCEPTED_REINSTATEMENT_ROUTES.INVOICES ||
						routeMatch?.pattern?.path === ACCEPTED_REINSTATEMENT_ROUTES.INVOICE,
				},
			},
		};

		return {
			...items,
		};
	};

	const initializeTree = () => {
		const sidePanelData: TreeData = {
			rootId: 'root',
			items: getItemsAccepted(),
		};

		return sidePanelData;
	};

	const tree = initializeTree();

	return <TreeNode defaultExpanded={[]} treeData={tree} />;
};

export default withCancellationContext<Props>(ReinstatementSidebar);
